import type { EligibilityResponse, VoteContext, PostId } from '../../types/pifster-contracts';
import type { VoteDomContextInput } from './vote-dom-scraper';
import type { VoteStateService } from './vote-state-service';

export type VoteIntentState =
  | 'ALREADY_VOTED'
  | 'AUTH_REQUIRED'
  | 'ELIGIBLE_VOTE'
  | 'ELIGIBLE_INTENT'
  | 'INELIGIBLE';

export interface VoteIntentOrchestratorDeps {
  readonly resolveApiBase: () => string;
  readonly stateService: VoteStateService;
  readonly openVoteModal: (context: VoteContext) => void;
  readonly redirectTo: (url: string) => void;
  readonly buildReturnStatusUrl: (statusCode: string, charityId: PostId, periodId: PostId) => string;
  readonly getLoginUrl: () => string;
}

function buildEligibilityUrl(apiBase: string, periodId: PostId): string {
  return `${apiBase}/vote/eligibility?period_id=${encodeURIComponent(String(periodId))}`;
}

function isEligibilityResponse(value: unknown): value is EligibilityResponse {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const candidate = value as Partial<EligibilityResponse>;
  return typeof candidate.eligible === 'boolean'
    && typeof candidate.alreadyVoted === 'boolean'
    && typeof candidate.requiredRoleMet === 'boolean'
    && Array.isArray(candidate.reasons)
    && typeof candidate.migration_required === 'boolean'
    && typeof candidate.email === 'string'
    && (typeof candidate.legacy_amount === 'number' || candidate.legacy_amount === null);
}

export async function fetchVoteEligibility(apiBase: string, periodId: PostId, nonce?: string): Promise<EligibilityResponse> {
  const fetchOptions: RequestInit = { credentials: 'include' };
  if (nonce) {
    fetchOptions.headers = { 'X-WP-Nonce': nonce };
  }
  const response = await fetch(buildEligibilityUrl(apiBase, periodId), fetchOptions);

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}`);
  }

  const payload: unknown = await response.json();
  if (!isEligibilityResponse(payload)) {
    throw new Error('Eligibility response contract validation failed.');
  }

  return payload;
}

export function resolveVoteIntentState(payload: EligibilityResponse, isLoggedIn: boolean): VoteIntentState {
  if (payload.alreadyVoted) {
    return 'ALREADY_VOTED';
  }

  if (!payload.eligible) {
    return payload.reasons.includes('auth_required') ? 'AUTH_REQUIRED' : 'INELIGIBLE';
  }

  return payload.requiredRoleMet ? 'ELIGIBLE_VOTE' : 'ELIGIBLE_INTENT';
}

export async function handleVoteIntent(
  deps: VoteIntentOrchestratorDeps,
  contextInput: VoteDomContextInput,
  nonce?: string,
): Promise<void> {
  const apiBase = deps.resolveApiBase();
  if (!apiBase) {
    if (deps.stateService.isLoggedIn() && contextInput.donateHref) {
      deps.redirectTo(contextInput.donateHref);
      return;
    }

    deps.openVoteModal({
      charityId: contextInput.charityId as unknown as PostId,
      charityName: contextInput.charityName,
      periodId: contextInput.periodId as unknown as PostId,
      periodLabel: contextInput.periodLabel,
    });
    return;
  }

  const eligibility = await fetchVoteEligibility(apiBase, contextInput.periodId as unknown as PostId, nonce);
  const state = resolveVoteIntentState(eligibility, deps.stateService.isLoggedIn());

  switch (state) {
    case 'ALREADY_VOTED':
      if (deps.stateService.isLoggedIn()) {
        deps.stateService.markPeriodVote(contextInput.periodId as unknown as PostId);
        deps.redirectTo(deps.buildReturnStatusUrl('already_voted', contextInput.charityId as unknown as PostId, contextInput.periodId as unknown as PostId));
      }
      return;
    case 'AUTH_REQUIRED':
      deps.openVoteModal({
        charityId: contextInput.charityId as unknown as PostId,
        charityName: contextInput.charityName,
        periodId: contextInput.periodId as unknown as PostId,
        periodLabel: contextInput.periodLabel,
      });
      return;
    case 'ELIGIBLE_VOTE':
      deps.redirectTo(deps.buildReturnStatusUrl('success', contextInput.charityId as unknown as PostId, contextInput.periodId as unknown as PostId));
      return;
    case 'ELIGIBLE_INTENT':
      deps.openVoteModal({
        charityId: contextInput.charityId as unknown as PostId,
        charityName: contextInput.charityName,
        periodId: contextInput.periodId as unknown as PostId,
        periodLabel: contextInput.periodLabel,
      });
      return;
    case 'INELIGIBLE':
      if (contextInput.donateHref) {
        deps.redirectTo(contextInput.donateHref);
        return;
      }
      deps.openVoteModal({
        charityId: contextInput.charityId as unknown as PostId,
        charityName: contextInput.charityName,
        periodId: contextInput.periodId as unknown as PostId,
        periodLabel: contextInput.periodLabel,
      });
  }
}
